import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  outputParams,
  REMOVED,
  TestSetupModule
} from '@nrwl/test-setup';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonMaterialModule } from '@nrwl/common-material';
import {
  CommonRegEx,
  Debounce,
  DevicesService,
  TestLayerValue,
  TestMode,
  UnmanagedDevicesService
} from '@nrwl/common-lib';
import { RFC2544SetupService } from '../../rfc2544-setup.service';
import { Subject, Subscription } from 'rxjs';
import { isNil, cloneDeep, findIndex } from 'lodash';
import {
  RFC2544ConfigFieldMapping as ConfigFieldMapping,
  CONFIRM_SELECTION,
  CVLAN_ETHER_TYPE,
  IP_ADDRESS_TYPE,
  IPAddressValue,
  LAYER_TYPES,
  LOOP_UP_SELECTION,
  LOOPBACK_TYPE,
  REMOTE_VLAN_TAG_COUNT_TYPE,
  SUBNET_MAP,
  TRANSMIT_INTERFACE_TYPE,
  TX_IMAGE,
  VLAN_CONFIG_TYPE,
  DEFINED_ADDRESS_TYPES,
  TRANSMIT_INTERFACE_VALUE,
  definedAddressValue
} from '../../models/models';

export const LoopbackTypes = {
  L2_LOOPBACK: 'l2-loopback',
  L3_IPV4_LOOPBACK: 'l3-ipv4-loopback',
  L3_IPV6_LOOPBACK: 'l3-ipv6-loopback'
};

@Component({
  selector: 'nrwl-rfc2544-endpoints',
  standalone: true,
  imports: [CommonModule, CommonMaterialModule, FormsModule, TestSetupModule],
  templateUrl: './rfc2544-endpoints.component.html',
  styleUrls: ['./rfc2544-endpoints.component.scss']
})
export class RFC2544EndpointsComponent implements OnInit, OnDestroy {
  @Input() tab: BaseSetupTab;
  @Input() setupCtrl: any;
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm', { static: true }) form: NgForm;

  config: any;
  editMode = false;
  newTestMode = true;
  cloneMode = false;
  outputs: Array<string>;
  selectedOriginator: any;
  selectedDestination: any;
  showOrginatorDetails = false;
  showDestinationDetails = false;
  tabsubscription: Subscription;
  selectedLoopbackTypes: string;
  configMapping = ConfigFieldMapping;
  testMode = TestMode.LOOPBACK;
  layer = LAYER_TYPES[0].value;
  ipAddressTypes = IP_ADDRESS_TYPE;
  vlanConfigTypes = VLAN_CONFIG_TYPE;
  cVlanEtherTypeData = CVLAN_ETHER_TYPE;
  deiEnableData = CONFIRM_SELECTION;
  remoteVlanData = REMOTE_VLAN_TAG_COUNT_TYPE;
  verifyVlanData = CONFIRM_SELECTION;
  lookbackType = LOOPBACK_TYPE;
  txTypeImage = TX_IMAGE.EQUIPMENT;
  txInterfaceTypes = TRANSMIT_INTERFACE_TYPE;
  definedAddressTypes = DEFINED_ADDRESS_TYPES;
  destDefinedAddressTypes = cloneDeep(DEFINED_ADDRESS_TYPES);
  suppressLoopTypes = LOOP_UP_SELECTION;
  ipType = IPAddressValue.IP_V4;
  subnetRegEx: RegExp;
  validIPV4RegEx: RegExp = CommonRegEx.IP_REGEX;
  validIPV6RegEx: RegExp = CommonRegEx.IPV6;
  validIPRegex: RegExp;
  subnetIPV4RegEx = CommonRegEx.SUBNET_MASK_REGEX;
  subnetIPV6RegEx = CommonRegEx.SUBNET_IPv6;
  macRegex = CommonRegEx.MAC;
  ipErrorMsg: string;
  agentType: string;
  etherTypesData = [];
  layer3Enabled = false;
  layer2Enabled = true;
  destTxTypeImage = TX_IMAGE.EQUIPMENT;
  isJmepAsLb = false;
  ipFields = {
    srcAssignedAddress: null,
    srcTestIpAddress: null,
    srcSubnet: null,
    srcGateway: null,
    destAssignedAddress: null,
    destTestIpAddress: null,
    destManualAddress: null
  };
  formField = {
    srcNetworkMac: null,
    srcIpAddress: null,
    srcIpV4Address: null,
    srcIpV6Address: null,
    srcSubnet: null,
    srcGateway: null,
    srcEditLayer2IP: true,
    srcEditLayer3IP: true,
    srcEditLayer2MAC: true,
    srcEditLayer3MAC: true,
    srcEditLayer3Subnet: true,
    destMacAddress: null,
    destIpAddress: null,
    destIpV4Address: null,
    destIpV6Address: null,
    destSubnet: null,
    destGateway: null,
    destShowIpLayer2: false,
    destShowMacLayer3: false,
    destDefinedAddress: null,
    destEquipmentMac: null,
    destNetworkMac: null,
    destTxInterface: null,
    vlanConfig: null,
    networkOverhead: null,
    svlan: null,
    svlanPriority: null,
    etherType: null,
    cvlan: null,
    cvlanPriority: null,
    cVlanEtherType: null,
    deiEnable: null,
    remoteVlan: null,
    vlanPriority: null,
    frameLossInterval: null,
    suppressLoop: null,
    loopbackType: null,
    swapMac: null,
    sendPriming: null
  };
  onAgentChanged = false;
  badIpValues = ['', '0.0.0.0', '255.255.255.255', '0', '::/0', undefined, null];
  readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    public setupService: RFC2544SetupService,
    private readonly deviceService: DevicesService,
    private readonly unmanagedDevicesService: UnmanagedDevicesService
  ) {}

  ngOnInit(): void {
    this.tab.form = this.form;
    if (this.setupService.tabDataChangeEvents) {
      this.tabsubscription = this.setupService.tabDataChangeEvents.subscribe(event => {
        if (event === 1) {
          if (this.testMode !== this.restoreFieldValues(ConfigFieldMapping.TEST_MODE)) {
            this.selectedOriginator = null;
            this.selectedDestination = null;
            this.showOrginatorDetails = false;
            this.showDestinationDetails = false;
          }
          this.restoreFormValues({ restoreAgentConfig: false });
        }
      });
    }
    this.restoreFormValues({ restoreAgentConfig: true });
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  restoreFormValues(paramConfig: any): void {
    this.newTestMode = this.setupService.mode === 'new';
    this.editMode = this.setupService.editMode;
    this.cloneMode = this.setupService.mode === 'clone';
    this.tab.form = this.form;
    this.restoreOtherTabValues();
    this.config = this.tab.config;
    this.outputs = this.config.def.outputs;
    this.selectedOriginator = this.restoreFieldValues(ConfigFieldMapping.SELECTED_ORIGINATOR);
    this.selectedDestination = this.restoreFieldValues(ConfigFieldMapping.SELECTED_DESTINATION);
    this.ipFields.srcTestIpAddress = this.restoreFieldValues(
      ConfigFieldMapping.SRC_TEST_IP_ADDRESS
    );
    this.formField.srcIpV6Address = this.restoreFieldValues(ConfigFieldMapping.SRC_IPV6_ADDRESS);
    this.formField.srcIpV4Address = this.restoreFieldValues(ConfigFieldMapping.SRC_IPV4_ADDRESS);
    this.formField.srcSubnet = this.restoreFieldValues(ConfigFieldMapping.SRC_SUBNET);
    this.formField.srcGateway = this.restoreFieldValues(ConfigFieldMapping.SRC_GATEWAY);
    this.formField.destTxInterface = this.restoreFieldValues(
      ConfigFieldMapping.DEST_TRANSMIT_INTERFACE
    );
    this.formField.destDefinedAddress = this.restoreFieldValues(
      ConfigFieldMapping.DEST_DEFINED_ADDRESS
    );
    this.formField.destIpV4Address = this.restoreFieldValues(ConfigFieldMapping.DEST_IPV4_ADDRESS);
    this.formField.destIpV6Address = this.restoreFieldValues(ConfigFieldMapping.DEST_IPV6_ADDRESS);
    this.formField.destMacAddress = this.restoreFieldValues(ConfigFieldMapping.DESTINATION_MAC);
    this.formField.destShowIpLayer2 = this.restoreFieldValues(
      ConfigFieldMapping.DEST_SHOW_IP_LAYER2
    );
    this.formField.destShowMacLayer3 = this.restoreFieldValues(
      ConfigFieldMapping.DEST_SHOW_MAC_LAYER3
    );
    this.formField.vlanConfig = this.restoreFieldValues(ConfigFieldMapping.VLAN_CONFIG);
    this.formField.networkOverhead = this.restoreFieldValues(ConfigFieldMapping.NETWORK_OVERHEAD);
    this.formField.svlan = this.restoreFieldValues(ConfigFieldMapping.SVLAN_ID);
    this.formField.svlanPriority = this.restoreFieldValues(ConfigFieldMapping.SVLAN_PRIORITY);
    this.formField.etherType = this.restoreFieldValues(ConfigFieldMapping.ETHER_TYPE);
    this.formField.cvlan = this.restoreFieldValues(ConfigFieldMapping.CVLAN_ID);
    this.formField.cvlanPriority = this.restoreFieldValues(ConfigFieldMapping.CVLAN_PRIORITY);
    this.formField.cVlanEtherType = this.restoreFieldValues(ConfigFieldMapping.CVLAN_ETHER_TYPE);
    this.etherTypesData = this.config.options[ConfigFieldMapping.ETHER_TYPE].value;
    this.formField.deiEnable = this.restoreFieldValues(ConfigFieldMapping.DEI_ENABLE);
    this.formField.remoteVlan = this.restoreFieldValues(ConfigFieldMapping.REMOTE_VLAN_TAG);
    this.formField.vlanPriority = this.restoreFieldValues(ConfigFieldMapping.VERIFY_PRIORITY);
    this.formField.srcIpV6Address = this.restoreFieldValues(ConfigFieldMapping.SRC_IPV6_ADDRESS);
    this.formField.srcIpV4Address = this.restoreFieldValues(ConfigFieldMapping.SRC_IPV4_ADDRESS);
    this.formField.srcSubnet = this.restoreFieldValues(ConfigFieldMapping.SRC_SUBNET);
    this.formField.srcGateway = this.restoreFieldValues(ConfigFieldMapping.SRC_GATEWAY);
    this.formField.suppressLoop = this.restoreFieldValues(
      ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS
    );
    this.formField.loopbackType = this.restoreFieldValues(ConfigFieldMapping.LOOPBACK_TYPE);
    if (paramConfig.restoreAgentConfig) {
      this.getDataBasedOnAgentConfig();
      this.checkIPAddressType();
      this.setManagedLoopbackDeviceValues();
      this.onAgentChanged = false;
    }
    this.checkLayerType();
  }

  restoreOtherTabValues(): void {
    this.ipType = this.restoreFieldValues(ConfigFieldMapping.IP_ADDRESS_TYPE);
    this.layer = this.restoreFieldValues(ConfigFieldMapping.LAYER_TO_TEST);
    this.testMode = this.restoreFieldValues(ConfigFieldMapping.TEST_MODE);
    if (this.layer === TestLayerValue.L2) {
      this.selectedLoopbackTypes = LoopbackTypes.L2_LOOPBACK;
    } else {
      this.selectedLoopbackTypes =
        this.ipType === IPAddressValue.IP_V6
          ? LoopbackTypes.L3_IPV6_LOOPBACK
          : LoopbackTypes.L3_IPV4_LOOPBACK;
    }
  }

  checkIPAddressType(): void {
    if (this.ipType === IPAddressValue.IP_V6) {
      if (this.newTestMode) {
        this.formField.srcGateway = this.validIPV6RegEx.test(this.formField.srcGateway)
          ? this.formField.srcGateway
          : '2000::';
        this.formField.srcSubnet = this.subnetIPV6RegEx.test(this.formField.srcSubnet)
          ? this.formField.srcSubnet
          : '64';
      }
      this.formField.srcIpAddress = this.formField.srcIpV6Address;
      this.formField.destIpAddress = this.formField.destIpV6Address;
      this.validIPRegex = this.validIPV6RegEx;
      this.subnetRegEx = this.subnetIPV6RegEx;
      this.ipErrorMsg = 'Address is not a valid IPv6 address';
    } else {
      if (this.onAgentChanged) {
        this.formField.srcGateway = this.ipFields.srcGateway;
        this.formField.srcSubnet = this.ipFields.srcSubnet;
        this.formField.srcIpAddress = this.ipFields.srcTestIpAddress;
      } else {
        this.formField.srcIpAddress =
          this.formField.srcIpV4Address || this.ipFields.srcTestIpAddress;
      }

      this.formField.destIpAddress = this.formField.destIpV4Address;
      this.validIPRegex = this.validIPV4RegEx;
      this.subnetRegEx = this.subnetIPV4RegEx;
      this.ipErrorMsg = 'Address is not a valid IPv4 address';
    }
    this.onSourceIpAddressChange();
    this.onDestinationIpAddressChange();
  }
  //eslint-disable-next-line
  onAgentChange(data: any): void {
    this.onAgentChanged = true;
    this.selectedOriginator = this.deviceService.findDeviceByID(data.originatorId);
    this.selectedDestination = this.deviceService.findDeviceByID(data.destinationId);
    if (this.selectedOriginator) {
      this.onDataEmit(ConfigFieldMapping.SELECTED_ORIGINATOR, this.selectedOriginator, true);
    }
    if (this.selectedDestination) {
      this.onDataEmit(
        ConfigFieldMapping.SELECTED_DESTINATION,
        this.selectedDestination,
        data.isValid
      );
    }
    this.agentType = this.selectedOriginator?.getModel() || this.selectedDestination?.getModel();
    this.restoreAgentBasedConfig(this.agentType, this.testMode);
    this.onDataEmit(ConfigFieldMapping.AGENT_TYPE, this.agentType, true);
    if (data.destinationId && data['engine-b-info']['agent-type'] === 'unmanaged') {
      this.isJmepAsLb = false;
      this.setUnManagedDeviceValues(data.destinationId);
    }
    if (
      data.destinationId &&
      this.testMode == TestMode.LOOPBACK &&
      data['engine-b-info']['agent-type'] !== 'unmanaged'
    ) {
      this.setManagedLoopbackDeviceValues();
    }
    this.restoreFormValues({ restoreAgentConfig: true });
  }

  setManagedLoopbackDeviceValues(): void {
    if (
      this.selectedDestination &&
      this.testMode == TestMode.LOOPBACK &&
      this.selectedDestination.deviceData?.type !== 'unmanaged'
    ) {
      this.onDataEmit(ConfigFieldMapping.SELECTED_DESTINATION, this.selectedDestination, true);
      if (this.selectedDestination.getModel()?.includes('JMEP')) {
        this.isJmepAsLb = true;
        this.formField.destEquipmentMac =
          this.selectedDestination.deviceData.capabilities?.phy?.['equipment-mac'];
        this.formField.destNetworkMac =
          this.selectedDestination.deviceData.capabilities?.phy?.['network-mac'];
        this.ipFields.destAssignedAddress =
          this.selectedDestination.deviceData.capabilities?.phy?.['assigned-ip'] || null;
        this.ipFields.destTestIpAddress =
          this.selectedDestination.deviceData.capabilities?.phy?.['test-ip'] || null;
        if (this.badIpValues.includes(this.ipFields.destTestIpAddress)) {
          const index = findIndex(this.destDefinedAddressTypes, {
            value: definedAddressValue.TEST_IP
          });
          if (index !== -1) {
            this.destDefinedAddressTypes[index].enabled = false;
            if (!this.badIpValues.includes(this.ipFields.destAssignedAddress)) {
              this.formField.destDefinedAddress = definedAddressValue.MANAGEMENT_IP;
            } else {
              this.formField.destDefinedAddress = definedAddressValue.MANUAL;
            }
          }
        }
        if (this.badIpValues.includes(this.ipFields.destAssignedAddress)) {
          const index = findIndex(this.destDefinedAddressTypes, {
            value: definedAddressValue.MANAGEMENT_IP
          });
          if (index !== -1) {
            this.destDefinedAddressTypes[index].enabled = false;
            if (!this.badIpValues.includes(this.ipFields.destTestIpAddress)) {
              this.formField.destDefinedAddress = definedAddressValue.TEST_IP;
            } else {
              this.formField.destDefinedAddress = definedAddressValue.MANUAL;
            }
          }
        }
        if (!this.newTestMode) {
          if (this.formField.destIpAddress === this.ipFields.destTestIpAddress) {
            this.formField.destDefinedAddress = definedAddressValue.TEST_IP;
          } else if (this.formField.destIpAddress === this.ipFields.destAssignedAddress) {
            this.formField.destDefinedAddress = definedAddressValue.MANAGEMENT_IP;
          } else {
            this.ipFields.destManualAddress = this.formField.destIpAddress;
            this.formField.destDefinedAddress = definedAddressValue.MANUAL;
          }
          if (this.formField.destMacAddress === this.formField.destEquipmentMac) {
            this.formField.destTxInterface = TRANSMIT_INTERFACE_VALUE.EQUIPMENT;
          } else {
            this.formField.destTxInterface = TRANSMIT_INTERFACE_VALUE.NETWORK;
          }
        } else {
          this.onDestDefinedAddressChange();
        }
        this.onDestTxInterfaceChange();
      } else {
        this.isJmepAsLb = false;
      }
      // this.formField.destMacAddress = this.selectedDestination.getModel()?.includes('JMEP')
      //   ? this.selectedDestination.deviceData.capabilities?.phy['equipment-mac']
      //   : this.selectedDestination.deviceData.capabilities?.phy['test-mac-address'];
      // this.formField.destIpAddress =
      //   this.selectedDestination.getIpAddress() ?? this.formField.destIpAddress;
      this.onDestinationMacAddressChange();
      this.onDestinationIpAddressChange();
    }
  }

  setUnManagedDeviceValues(deviceId: string): void {
    this.selectedDestination = this.unmanagedDevicesService.findDeviceByID(deviceId);
    this.showDestinationDetails = true;

    if (this.selectedDestination) {
      this.onDataEmit(ConfigFieldMapping.SELECTED_DESTINATION, this.selectedDestination, true);
      this.formField.destMacAddress =
        this.selectedDestination.deviceData.capabilities?.['l2-loopback']?.['mac'] ??
        this.formField.destMacAddress;
      this.formField.destIpAddress =
        this.selectedDestination.deviceData.capabilities?.['l3-ipv4-loopback']?.['ip'] ??
        this.formField.destIpAddress;
      if (this.ipType === IPAddressValue.IP_V6) {
        this.formField.destIpAddress =
          this.selectedDestination.deviceData.capabilities?.['l3-ipv6-loopback']?.['ip'] ??
          this.formField.destIpAddress;
      }
      this.onDestinationMacAddressChange();
      this.onDestinationIpAddressChange();
    }
  }

  onDestinationMacAddressChange(): void {
    this.onDataEmit(ConfigFieldMapping.DESTINATION_MAC, this.formField.destMacAddress, true);
  }

  onDestinationIpAddressChange(): void {
    if (this.ipType === IPAddressValue.IP_V4) {
      this.onDataEmit(ConfigFieldMapping.DEST_IPV4_ADDRESS, this.formField.destIpAddress, true);
    } else {
      this.onDataEmit(ConfigFieldMapping.DEST_IPV6_ADDRESS, this.formField.destIpAddress, true);
    }
  }

  showFormField(field: string): boolean {
    return this.outputs.includes(field);
  }

  onDestTxInterfaceChange(): void {
    this.destTxTypeImage =
      this.formField.destTxInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? TX_IMAGE.EQUIPMENT
        : TX_IMAGE.NETWORK;
    this.formField.destMacAddress =
      this.formField.destTxInterface === TRANSMIT_INTERFACE_VALUE.EQUIPMENT
        ? this.formField.destEquipmentMac
        : this.formField.destNetworkMac;
    this.onDestinationMacAddressChange();
    this.onDataEmit(
      ConfigFieldMapping.DEST_TRANSMIT_INTERFACE,
      this.formField.destTxInterface,
      true
    );
  }

  onDestDefinedAddressChange(): void {
    if (this.formField.destDefinedAddress === definedAddressValue.TEST_IP) {
      this.formField.destIpAddress = this.ipFields.destTestIpAddress;
    } else if (this.formField.destDefinedAddress === definedAddressValue.MANAGEMENT_IP) {
      this.formField.destIpAddress = this.ipFields.destAssignedAddress;
    } else {
      this.formField.destIpAddress =
        this.ipFields.destManualAddress || this.ipFields.destAssignedAddress;
    }
    this.onDataEmit(
      ConfigFieldMapping.DEST_DEFINED_ADDRESS,
      this.formField.destDefinedAddress,
      true
    );
    this.onDestinationIpAddressChange();
  }

  onVlanConfigChange(): void {
    this.onDataEmit(ConfigFieldMapping.VLAN_CONFIG, this.formField.vlanConfig, true);
    this.formField.svlan = '';
    this.formField.cvlan = '';
    this.onDataEmit(ConfigFieldMapping.SVLAN_ID, this.formField.svlan, true);
    this.onDataEmit(ConfigFieldMapping.CVLAN_ID, this.formField.cvlan, true);
  }

  networkOverheadChange(): void {
    this.onDataEmit(ConfigFieldMapping.NETWORK_OVERHEAD, this.formField.networkOverhead, true);
  }

  onSVlanChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.SVLAN_ID,
      this.formField.svlan,
      this.form.controls['svlan']?.valid
    );
  }

  onSVlanPriorityChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.SVLAN_PRIORITY,
      this.formField.svlanPriority,
      this.form.controls['sVlanPriorityInput']?.valid
    );
  }

  onEtherTypeChange(): void {
    this.onDataEmit(ConfigFieldMapping.ETHER_TYPE, this.formField.etherType, true);
  }

  onCVlanChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.CVLAN_ID,
      this.formField.cvlan,
      this.form.controls['cvlan']?.valid
    );
  }

  onCVlanPriorityChange(): void {
    this.onDataEmit(
      ConfigFieldMapping.CVLAN_PRIORITY,
      this.formField.cvlanPriority,
      this.form.controls['cVlanPriorityInput']?.valid
    );
  }

  onCVlanEtherTypeChange(): void {
    this.onDataEmit(ConfigFieldMapping.CVLAN_ETHER_TYPE, this.formField.cVlanEtherType, true);
  }

  onDeiEnableChange(): void {
    this.onDataEmit(ConfigFieldMapping.DEI_ENABLE, this.formField.deiEnable, true);
  }

  onRemoteVlanChange(): void {
    this.onDataEmit(ConfigFieldMapping.REMOTE_VLAN_TAG, this.formField.remoteVlan, true);
  }

  onVlanPriorityChange(): void {
    this.onDataEmit(ConfigFieldMapping.VERIFY_PRIORITY, this.formField.vlanPriority, true);
  }
  //eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  getDataBasedOnAgentConfig(): void {
    if (this.selectedOriginator) {
      this.showOrginatorDetails = true;
      this.showDestinationDetails = this.testMode == TestMode.LOOPBACK;
      this.setupService.setOriginatorAgent(this.selectedOriginator);
      const capabilityData = this.setupService.getAgentLookUpData(
        this.config.lookup,
        this.selectedOriginator
      );
      this.ipFields.srcTestIpAddress =
        capabilityData[ConfigFieldMapping.SRC_TEST_IP_ADDRESS] ?? this.ipFields.srcTestIpAddress;
      this.ipFields.srcSubnet = capabilityData[ConfigFieldMapping.SRC_SUBNET]
        ? this.findSubnetMapValue(capabilityData[ConfigFieldMapping.SRC_SUBNET])
        : this.formField.srcSubnet;
      this.ipFields.srcGateway =
        capabilityData[ConfigFieldMapping.SRC_GATEWAY] ?? this.formField.srcGateway;
      this.onSourceIpAddressChange();
      this.onSourceGatewayChange();
      this.onSourceSubnetChange();
    }
    if (this.selectedDestination) {
      this.setupService.setDestinationAgent(this.selectedDestination);
      this.showDestinationDetails = true;
      if (this.onAgentChanged) {
        const capabilityData = this.setupService.getAgentLookUpData(
          this.config.lookup,
          this.selectedDestination
        );
        this.formField.destIpV4Address =
          capabilityData[ConfigFieldMapping.DEST_IPV4_ADDRESS] ?? this.formField.destIpV4Address;
        this.formField.destMacAddress =
          capabilityData[ConfigFieldMapping.DESTINATION_MAC] ?? this.formField.destMacAddress;
        this.onDestinationIpAddressChange();
        this.onDestinationMacAddressChange();
      }
    }
  }

  findSubnetMapValue(subnet: string): string {
    subnet = subnet.toString();
    if (subnet && !subnet.includes('.')) {
      return SUBNET_MAP[subnet] ?? subnet;
    }
    return subnet;
  }

  @Debounce(500)
  onSourceIpAddressChange(): void {
    if (this.ipType === IPAddressValue.IP_V4) {
      this.onDataEmit(ConfigFieldMapping.SRC_IPV4_ADDRESS, this.formField.srcIpAddress, true);
    } else {
      this.onDataEmit(ConfigFieldMapping.SRC_IPV6_ADDRESS, this.formField.srcIpAddress, true);
    }
  }

  @Debounce(500)
  onSourceGatewayChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_GATEWAY, this.formField.srcGateway, true);
  }

  @Debounce(500)
  onSourceSubnetChange(): void {
    this.onDataEmit(ConfigFieldMapping.SRC_SUBNET, this.formField.srcSubnet, true);
  }

  onSuppressLoopChange(): void {
    this.onDataEmit(ConfigFieldMapping.SUPPRESS_LOOP_COMMANDS, this.formField.suppressLoop, true);
  }

  loopbackTypeChange(): void {
    this.onDataEmit(ConfigFieldMapping.LOOPBACK_TYPE, this.formField.loopbackType, true);
  }

  checkLayerType(): void {
    this.layer3Enabled = this.layer === TestLayerValue.L3;
    this.layer2Enabled = this.layer === TestLayerValue.L2;
  }
  //eslint-disable-next-line
  setManualDestAddress(event): void {
    event.preventDefault();
    this.showDestinationDetails = true;
  }

  ngOnDestroy(): void {
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
  }
}
